/**
 * OBK-7 (K44) — durable "Needs Confirmation" queue hook.
 *
 * Unlike `useConfirmationAlerts` (a transient 30s toast on INSERT), this hook
 * QUERIES the persisted `tickets.confirmation_status` column, so the operator
 * alert survives refresh / re-login / device changes. Role-gated: it does NOT
 * query (no customer PII fetched) unless the caller's role may view the queue.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  NEEDS_CONFIRMATION_ACTIVE_STATUSES,
  canViewNeedsConfirmationQueue,
  filterActiveNeedsConfirmation,
  nextConfirmationStatus,
  type NeedsConfirmationTicket,
  type ResolveAction,
} from '@/lib/optiqueue/needsConfirmationQueue';

interface UseNeedsConfirmationQueue {
  items: NeedsConfirmationTicket[];
  loading: boolean;
  canView: boolean;
  refetch: () => Promise<void>;
  resolve: (ticketId: string, action: ResolveAction) => Promise<void>;
}

export function useNeedsConfirmationQueue(
  shopId: string | null | undefined,
  role: string | null | undefined,
): UseNeedsConfirmationQueue {
  const canView = canViewNeedsConfirmationQueue(role);
  const [items, setItems] = useState<NeedsConfirmationTicket[]>([]);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    // Fail-closed: never fetch customer PII for a role that may not view it,
    // and never query without a shop scope.
    if (!canView || !shopId) {
      setItems([]);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('tickets')
      .select(
        'id, confirmation_status, customer_name, customer_phone, customer_request_notes, intent, start_time',
      )
      .eq('shop_id', shopId)
      .in('confirmation_status', NEEDS_CONFIRMATION_ACTIVE_STATUSES as unknown as string[])
      .order('start_time', { ascending: true });
    if (error) {
      console.error('[useNeedsConfirmationQueue] query failed:', error.message);
      setLoading(false);
      return;
    }
    // Defence-in-depth: re-filter client-side so a confirmed row can never
    // linger in the active queue even if the query filter changes.
    setItems(filterActiveNeedsConfirmation((data ?? []) as NeedsConfirmationTicket[]));
    setLoading(false);
  }, [canView, shopId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Durable + live: refetch on any ticket change for the shop so a resolve
  // from another device drops the item here too.
  useEffect(() => {
    if (!canView || !shopId) return;
    const channel = supabase
      .channel(`needs-confirmation-${shopId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tickets', filter: `shop_id=eq.${shopId}` },
        () => {
          refetch();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [canView, shopId, refetch]);

  const resolve = useCallback(
    async (ticketId: string, action: ResolveAction) => {
      if (!canView) return; // fail-closed: unauthorized roles cannot resolve
      const next = nextConfirmationStatus(action);
      // Optimistic drop when fully confirmed; otherwise update local status.
      setItems((prev) =>
        next === 'confirmed'
          ? prev.filter((t) => t.id !== ticketId)
          : prev.map((t) => (t.id === ticketId ? { ...t, confirmation_status: next } : t)),
      );
      const { error } = await supabase
        .from('tickets')
        .update({ confirmation_status: next })
        .eq('id', ticketId)
        .eq('shop_id', shopId ?? '');
      if (error) {
        console.error('[useNeedsConfirmationQueue] resolve failed:', error.message);
        await refetch(); // reconcile on failure
      }
    },
    [canView, shopId, refetch],
  );

  return { items, loading, canView, refetch, resolve };
}
